'use client';

import { useState, useEffect } from 'react';
import { X, MessageSquare, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import './ModalReponseProbleme.css';
import { useReadFeedbacks } from '@/hooks/useReadFeedback';

interface UserFeedback {
  id: number;
  userId: number;
  feedbackType: "note" | "probleme";
  note: number | null;
  commentaire: string | null;
  problemeType: string | null;
  message: string | null;
  dateCreated: Date;
  suiviStatus: "non_traité" | "en_cours" | "résolu" | null;
  suiviCommentaire: string | null;
  dateTreated: Date | null;
  lu: boolean;
}

interface ModalReponseProblemeProps {
  open: boolean;
  onClose: () => void;
}

export default function ModalReponseProbleme({ open, onClose }: ModalReponseProblemeProps) {
  const { feedbacks, loading, error, loadFeedbacks } = useReadFeedbacks();
  const [selectedFeedback, setSelectedFeedback] = useState<UserFeedback | null>(null);

  // Charger les feedbacks quand le modal s'ouvre
  useEffect(() => {
    if (open) {
      loadFeedbacks();
    }
  }, [open, loadFeedbacks]);

  const handleSelectFeedback = (feedback: UserFeedback) => {
    setSelectedFeedback(feedback);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'résolu':
        return <CheckCircle size={18} className="status-icon status-icon-resolu" />;
      case 'en_cours':
        return <Clock size={18} className="status-icon status-icon-en-cours" />;
      case 'non_traité':
        return <AlertCircle size={18} className="status-icon status-icon-non-traite" />;
      default:
        return <AlertCircle size={18} className="status-icon" />;
    }
  };

  const getStatusLabel = (status: string | null) => {
    switch (status) {
      case 'résolu':
        return 'Résolu';
      case 'en_cours':
        return 'En cours';
      case 'non_traité':
        return 'Non traité';
      default:
        return 'Non traité';
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-reponse-probleme" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <MessageSquare size={24} className="modal-header-icon" />
            <div>
              <h2 className="modal-title">Réponses aux problèmes</h2>
              <p className="modal-subtitle">Historique de vos signalements traités</p>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Fermer">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {error && (
            <div className="alert alert-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Chargement des réponses...</p>
            </div>
          ) : feedbacks.length === 0 ? (
            <div className="empty-state">
              <MessageSquare size={48} className="empty-icon" />
              <p className="empty-title">Aucune réponse disponible</p>
              <p className="empty-subtitle">Vous n'avez pas encore de problème traité avec une réponse.</p>
            </div>
          ) : (
            <div className="content-grid">
              {/* Liste des feedbacks */}
              <div className="feedbacks-list">
                <h3 className="section-title">Vos signalements ({feedbacks.length})</h3>
                <div className="feedbacks-scroll">
                  {feedbacks.map((feedback) => (
                    <div
                      key={feedback.id}
                      className={`feedback-card ${selectedFeedback?.id === feedback.id ? 'active' : ''}`}
                      onClick={() => handleSelectFeedback(feedback)}
                    >
                      <div className="feedback-card-header">
                        <span className="feedback-type">{feedback.problemeType}</span>
                        {getStatusIcon(feedback.suiviStatus)}
                      </div>
                      <p className="feedback-message">
                        {feedback.message?.substring(0, 80)}
                        {feedback.message && feedback.message.length > 80 ? '...' : ''}
                      </p>
                      <div className="feedback-card-footer">
                        <span className="feedback-date">{formatDate(feedback.dateCreated)}</span>
                        <span className={`feedback-status status-${feedback.suiviStatus || 'non_traité'}`}>
                          {getStatusLabel(feedback.suiviStatus)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Détails du feedback sélectionné */}
              {selectedFeedback ? (
                <div className="feedback-details">
                  <h3 className="section-title">Détails du signalement</h3>
                  
                  <div className="detail-section">
                    <div className="detail-row">
                      <span className="detail-label">Type de problème :</span>
                      <span className="detail-value">{selectedFeedback.problemeType}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Date :</span>
                      <span className="detail-value">{formatDate(selectedFeedback.dateCreated)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Statut :</span>
                      <div className="detail-value">
                        {getStatusIcon(selectedFeedback.suiviStatus)}
                        <span className={`status-text status-${selectedFeedback.suiviStatus || 'non_traité'}`}>
                          {getStatusLabel(selectedFeedback.suiviStatus)}
                        </span>
                      </div>
                    </div>
                    {selectedFeedback.dateTreated && (
                      <div className="detail-row">
                        <span className="detail-label">Traité le :</span>
                        <span className="detail-value">{formatDate(selectedFeedback.dateTreated)}</span>
                      </div>
                    )}
                  </div>

                  <div className="message-section">
                    <h4 className="message-title">Votre message</h4>
                    <div className="message-box message-user">
                      {selectedFeedback.message || 'Aucun message'}
                    </div>
                  </div>

                  {selectedFeedback.commentaire && (
                    <div className="message-section">
                      <h4 className="message-title">Commentaire additionnel</h4>
                      <div className="message-box message-user">
                        {selectedFeedback.commentaire}
                      </div>
                    </div>
                  )}

                  {selectedFeedback.suiviCommentaire && (
                    <div className="message-section">
                      <h4 className="message-title">
                        <CheckCircle size={18} className="title-icon" />
                        Réponse de notre équipe
                      </h4>
                      <div className="message-box message-response">
                        {selectedFeedback.suiviCommentaire}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="feedback-details-placeholder">
                  <MessageSquare size={48} className="placeholder-icon" />
                  <p className="placeholder-text">Sélectionnez un signalement pour voir les détails</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-close-modal" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}